import { Muscle, MuscleData, GameType, GameQuestion } from '../types';

// シード付きランダム生成器
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }
}

export const shuffleArray = <T>(array: T[], seed: number): T[] => {
  const shuffled = [...array];
  const rng = new SeededRandom(seed);
  
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
};

export const getAllMuscles = (muscleData: MuscleData): Muscle[] => {
  return Object.values(muscleData).flat();
};

export const generateQuestions = (
  muscleData: MuscleData,
  gameType: GameType,
  count: number = 15,
  seed: number = Date.now()
): GameQuestion[] => {
  const allMuscles = getAllMuscles(muscleData);
  const shuffledMuscles = shuffleArray(allMuscles, seed);
  const selectedMuscles = shuffledMuscles.slice(0, count);
  
  return selectedMuscles.map((muscle, index) => {
    const questionId = `${gameType}_${index}_${seed}`;
    
    switch (gameType) {
      case GameType.NAME_MATCH:
        return generateNameMatchQuestion(questionId, muscle, allMuscles, seed + index);
      
      case GameType.INNERVATION:
        return generateInnervationQuestion(questionId, muscle, allMuscles, seed + index);
      
      case GameType.BLOOD_SUPPLY:
        return generateBloodSupplyQuestion(questionId, muscle, allMuscles, seed + index);
      
      case GameType.INNERVATION_OR_BLOOD:
        return generateInnervationOrBloodQuestion(questionId, muscle, allMuscles, seed + index);
      
      case GameType.ORIGIN_INSERTION:
        return generateOriginInsertionQuestion(questionId, muscle, allMuscles, seed + index);
      
      default:
        throw new Error(`Unknown game type: ${gameType}`);
    }
  });
};

const generateNameMatchQuestion = (
  id: string,
  muscle: Muscle,
  allMuscles: Muscle[],
  seed: number
): GameQuestion => {
  const rng = new SeededRandom(seed);
  const isJpToEn = rng.next() > 0.5;
  
  const question = isJpToEn ? muscle.name_jp : muscle.name_en;
  const correctAnswer = isJpToEn ? muscle.name_en : muscle.name_jp;
  
  const wrongOptions = allMuscles
    .filter(m => m !== muscle)
    .map(m => isJpToEn ? m.name_en : m.name_jp)
    .filter(name => name !== correctAnswer);
  
  const shuffledWrongOptions = shuffleArray(wrongOptions, seed);
  const options = shuffleArray([correctAnswer, ...shuffledWrongOptions.slice(0, 3)], seed + 1);
  
  return {
    id,
    muscle,
    question: `${isJpToEn ? '日本語' : '英語'}「${question}」に対応する${isJpToEn ? '英語' : '日本語'}は？`,
    options,
    correctAnswer,
    type: GameType.NAME_MATCH
  };
};

const generateInnervationQuestion = (
  id: string,
  muscle: Muscle,
  allMuscles: Muscle[],
  seed: number
): GameQuestion => {
  const rng = new SeededRandom(seed);
  const useJpName = rng.next() > 0.5;
  
  const muscleName = useJpName ? muscle.name_jp : muscle.name_en;
  const correctAnswer = muscle.innervation;
  
  const wrongOptions = allMuscles
    .filter(m => m !== muscle && m.innervation !== correctAnswer)
    .map(m => m.innervation);
  
  const uniqueWrongOptions = Array.from(new Set(wrongOptions));
  const shuffledWrongOptions = shuffleArray(uniqueWrongOptions, seed);
  const options = shuffleArray([correctAnswer, ...shuffledWrongOptions.slice(0, 3)], seed + 1);
  
  return {
    id,
    muscle,
    question: `「${muscleName}」の神経支配は？`,
    options,
    correctAnswer,
    type: GameType.INNERVATION
  };
};

const generateBloodSupplyQuestion = (
  id: string,
  muscle: Muscle,
  allMuscles: Muscle[],
  seed: number
): GameQuestion => {
  const rng = new SeededRandom(seed);
  const useJpName = rng.next() > 0.5;
  
  const muscleName = useJpName ? muscle.name_jp : muscle.name_en;
  const correctAnswer = muscle.blood_supply;
  
  const wrongOptions = allMuscles
    .filter(m => m !== muscle && m.blood_supply !== correctAnswer)
    .map(m => m.blood_supply);
  
  const uniqueWrongOptions = Array.from(new Set(wrongOptions));
  const shuffledWrongOptions = shuffleArray(uniqueWrongOptions, seed);
  const options = shuffleArray([correctAnswer, ...shuffledWrongOptions.slice(0, 3)], seed + 1);
  
  return {
    id,
    muscle,
    question: `「${muscleName}」の血液供給は？`,
    options,
    correctAnswer,
    type: GameType.BLOOD_SUPPLY
  };
};

const generateInnervationOrBloodQuestion = (
  id: string,
  muscle: Muscle,
  allMuscles: Muscle[],
  seed: number
): GameQuestion => {
  const rng = new SeededRandom(seed);
  const useJpName = rng.next() > 0.5;
  const askInnervation = rng.next() > 0.5;
  
  const muscleName = useJpName ? muscle.name_jp : muscle.name_en;
  const correctAnswer = askInnervation ? muscle.innervation : muscle.blood_supply;
  const questionType = askInnervation ? '神経支配' : '血液供給';
  
  const wrongOptions = allMuscles
    .filter(m => m !== muscle)
    .map(m => askInnervation ? m.innervation : m.blood_supply)
    .filter(option => option !== correctAnswer);
  
  const uniqueWrongOptions = Array.from(new Set(wrongOptions));
  const shuffledWrongOptions = shuffleArray(uniqueWrongOptions, seed);
  const options = shuffleArray([correctAnswer, ...shuffledWrongOptions.slice(0, 3)], seed + 1);
  
  return {
    id,
    muscle,
    question: `「${muscleName}」の${questionType}は？`,
    options,
    correctAnswer,
    type: GameType.INNERVATION_OR_BLOOD
  };
};

const generateOriginInsertionQuestion = (
  id: string,
  muscle: Muscle,
  allMuscles: Muscle[],
  seed: number
): GameQuestion => {
  const rng = new SeededRandom(seed);
  const useJpName = rng.next() > 0.5;
  const askOrigin = rng.next() > 0.5;
  
  const muscleName = useJpName ? muscle.name_jp : muscle.name_en;
  const correctAnswer = askOrigin ? muscle.origin : muscle.insertion;
  const questionType = askOrigin ? '起始' : '停止';
  
  const wrongOptions = allMuscles
    .filter(m => m !== muscle)
    .map(m => askOrigin ? m.origin : m.insertion)
    .filter(option => option !== correctAnswer);
  
  const uniqueWrongOptions = Array.from(new Set(wrongOptions));
  const shuffledWrongOptions = shuffleArray(uniqueWrongOptions, seed);
  const options = shuffleArray([correctAnswer, ...shuffledWrongOptions.slice(0, 3)], seed + 1);
  
  return {
    id,
    muscle,
    question: `「${muscleName}」の${questionType}は？`,
    options,
    correctAnswer,
    type: GameType.ORIGIN_INSERTION
  };
};